import { forceShowModalLocal, unseeModalLocal } from '@/lib/modal-debugger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Button } from '@heidi/design-system/button';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { DICTATE_REENGAGEMENT_CARD_MODAL_ID } from '@heidi/dictate';
import {
  type DictateReengagementTrigger,
  dictateReengagementDevTriggerOverrideAtom,
} from '@heidi/dictate/data';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

const VARIANT_SEGMENTS: { value: DictateReengagementTrigger; label: string }[] = [
  { value: 'inline', label: 'Inline' },
  { value: 'recording', label: 'Recording' },
];

interface DictateReengagementDevSectionProps {
  onRequestClose?: () => void;
}

export function DictateReengagementDevSection({
  onRequestClose,
}: Readonly<DictateReengagementDevSectionProps>) {
  const [triggerOverride, setTriggerOverride] = useAtom(
    dictateReengagementDevTriggerOverrideAtom
  );

  const isNonProd = getEnvironment() !== ENV_NAME.PROD;
  const variant: DictateReengagementTrigger = triggerOverride ?? 'inline';

  const handleVariantChange = useCallback(
    (next: string) => {
      setTriggerOverride(next as DictateReengagementTrigger);
    },
    [setTriggerOverride]
  );

  const handleShow = useCallback(() => {
    setTriggerOverride(variant);
    unseeModalLocal(DICTATE_REENGAGEMENT_CARD_MODAL_ID);
    forceShowModalLocal(DICTATE_REENGAGEMENT_CARD_MODAL_ID);
    onRequestClose?.();
  }, [onRequestClose, setTriggerOverride, variant]);

  const handleReset = useCallback(() => {
    unseeModalLocal(DICTATE_REENGAGEMENT_CARD_MODAL_ID);
  }, []);

  if (!isNonProd) {
    return null;
  }

  return (
    <section
      aria-labelledby="dev-tools-dictate-reengagement-heading"
      className="mt-4 border-t border-border pt-4"
    >
      <TypographyP2SemiBold id="dev-tools-dictate-reengagement-heading" className="mb-1">
        Dictate re-engagement
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Preview the post-dictation card without completing a dictation. Variant selects
        copy: Inline (mic) vs Recording (green button).
      </TypographyCaption>

      <div className="flex flex-col gap-3">
        <TypographyCaption className="text-text-secondary">Variant</TypographyCaption>
        <SegmentedControl value={variant} onValueChange={handleVariantChange}>
          <SegmentedControlList className="grid w-full grid-cols-2 gap-1">
            {VARIANT_SEGMENTS.map((opt) => (
              <SegmentedControlTrigger
                key={opt.value}
                value={opt.value}
                data-testid={`dev-tools-dictate-reengagement-variant-${opt.value}`}
              >
                {opt.label}
              </SegmentedControlTrigger>
            ))}
          </SegmentedControlList>
        </SegmentedControl>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="pro"
            size="sm"
            className="flex-1"
            data-testid="dev-tools-show-dictate-reengagement"
            onClick={handleShow}
          >
            Show card
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            data-testid="dev-tools-reset-dictate-reengagement"
            onClick={handleReset}
          >
            Reset seen stamp
          </Button>
        </div>
      </div>
    </section>
  );
}
